'use client';

import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Standard page header pattern used across every L3 sidebar-app page.
 * Structure: optional eyebrow → display-font H1 → optional subtitle →
 * optional children (actions/slots) → bottom divider with breathing room.
 *
 *   <PageHeader
 *     eyebrow="Allocations"
 *     title="Plan the month"
 *     subtitle="Who's doing what, and when."
 *   />
 *
 * Apps are expected to render exactly one PageHeader at the top of each
 * page. Keeps typography and spacing consistent across the whole
 * ecosystem (platform.loop3.ai, admin.loop3.ai, future apps).
 */

interface PageHeaderProps {
  /** Small uppercased mono label above the title (e.g. route name / section). */
  eyebrow?: React.ReactNode;
  /** Main page title — always rendered in the display font. */
  title: React.ReactNode;
  /**
   * For pages whose navigation already names them: the title is for screen readers only, and the
   * header shrinks to its actions, left-aligned, with no divider. `eyebrow` and `subtitle` are not rendered.
   */
  hideTitle?: boolean;
  /** Optional one-sentence description below the title. */
  subtitle?: React.ReactNode;
  /** Optional right-aligned slot for actions (e.g. a primary button). */
  actions?: React.ReactNode;
  /** Extra content below the subtitle, inside the header block. */
  children?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  hideTitle,
  subtitle,
  actions,
  children,
  className,
}: PageHeaderProps) {
  if (hideTitle) {
    return (
      <div className={cn((actions || children) && 'mb-6', className)}>
        <h1 className="sr-only">{title}</h1>
        {actions && <div className="flex">{actions}</div>}
        {children}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'mb-6 border-b border-border pb-6',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <span className="eyebrow opacity-60">{eyebrow}</span>
          )}
          <h1
            className={cn(
              'font-display text-display-sm font-light leading-[1.05]',
              eyebrow && 'mt-2',
            )}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 max-w-xl text-sm opacity-70">{subtitle}</p>
          )}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
